import React, { useCallback, useEffect, useMemo, useState, useContext } from "react";

// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { getSessionId } from "@cart-client/helpers/Cart";

// types
import { ICroutonListData, GenericCroutonProps } from "@swiggy-private/cart-types";

// styles
import { Container, CroutonText } from "./styles";

// constants
import { DEAL_RUSH_CROUTON_CLOSED_SID } from "@cart-client/constants/index";
import { CLOSE_ICON_CSS_PROPERTIES, DEAL_RUSH_CROUTON_DATA_TEST_IDS as IDS, DEAL_RUSH_OFFER } from "./constants";

//dls
import { SpriteIconWeb } from "@swiggy-private/dls-web-icons/dist/SpriteIconWeb";

// context
import { AnalyticsContext } from "@swiggy-private/checkout-core";

//analytics
import {
    dealRushImpressionEvent,
    dealRushExpiredImpressionEvent,
    dealRushClickEvent,
} from "@cart-client/analytics/events/DealRush";

export const useDealRushCrouton = (croutonData?: ICroutonListData) => {
    const [croutonText, setCroutonText] = useState<string>(croutonData?.text || "");
    const [croutonIcon, setCroutonIcon] = useState<string>(croutonData?.icon || "");
    const [showTimer, setShowTimer] = useState<boolean>(croutonData?.showTimer || false);
    const [showCloseIcon, setShowCloseIcon] = useState<boolean>(true);
    const expiredState = croutonData?.states?.["EXPIRED"]?.textPresentation?.[0];

    const dealRushClosedSid = localStorage.getItem(DEAL_RUSH_CROUTON_CLOSED_SID);

    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);

    const sid = getSessionId();

    useEffect(() => {
        setCroutonText(croutonData?.text || "");
        setCroutonIcon(croutonData?.icon || "");
        setShowTimer(croutonData?.showTimer || false);
    }, [croutonData?.text, croutonData?.icon, croutonData?.showTimer]);

    const onCompleteTimer = useCallback(() => {
        setCroutonText(expiredState?.text || "");
        setCroutonIcon(expiredState?.icon || "");
        setShowTimer(false);
        setShowCloseIcon(false);
    }, [expiredState, setShowTimer]);

    const onCloseIconClick = useCallback(() => {
        localStorage.setItem(DEAL_RUSH_CROUTON_CLOSED_SID, sid);
        dealRushClickEvent({ text: croutonText || "", clickEvent, timer: croutonData?.timer, offer: DEAL_RUSH_OFFER });
    }, [sid, clickEvent, croutonText, croutonData?.timer]);

    const handleImpressionEvent = useCallback(() => {
        showTimer
            ? dealRushImpressionEvent({
                  text: croutonText || "",
                  impressionEvent,
                  timer: croutonData?.timer,
                  offer: DEAL_RUSH_OFFER,
              })
            : dealRushExpiredImpressionEvent(croutonText || "", impressionEvent, DEAL_RUSH_OFFER);
    }, [showTimer, croutonText, impressionEvent, croutonData?.timer]);

    const titleNode = useMemo(() => {
        if (!isEmpty(croutonText)) {
            return (
                <Container showTimer={showTimer} data-testid={IDS.TEXT}>
                    <CroutonText>{croutonText}</CroutonText>
                </Container>
            );
        }
        return null;
    }, [croutonText, showTimer]);

    const cssProperties = useMemo(
        () => (croutonData?.backgroundColor ? { backgroundColor: croutonData.backgroundColor } : undefined),
        [croutonData?.backgroundColor],
    );

    const closeIcon = useMemo(
        () => (showCloseIcon ? <SpriteIconWeb iconName="close12" fillColor="text_inverse_Low_Emphasis" /> : null),
        [showCloseIcon],
    );

    const payloadForCroutonRegister: GenericCroutonProps = useMemo(() => {
        return {
            title: titleNode,
            icon: croutonIcon || "",
            showTimer: showTimer,
            duration: croutonData?.timer,
            durationOfCrouton: showCloseIcon ? 0 : croutonData?.timeout,
            onCompleteTimer,
            customWrapperDataTestId: IDS.CONTAINER,
            timerTextColor: croutonData?.timerTextColor,
            textColor: croutonData?.textColor,
            cssProperties,
            closeIcon,
            closeIconCssProperties: CLOSE_ICON_CSS_PROPERTIES,
            onClose: showCloseIcon ? onCloseIconClick : undefined,
            impressionEvent: handleImpressionEvent,
        };
    }, [
        croutonData,
        onCompleteTimer,
        titleNode,
        croutonIcon,
        showTimer,
        showCloseIcon,
        onCloseIconClick,
        handleImpressionEvent,
        cssProperties,
        closeIcon,
    ]);

    if (isEmpty(croutonData) || (!isEmpty(dealRushClosedSid) && sid === dealRushClosedSid)) {
        return null;
    }

    return payloadForCroutonRegister;
};
